import { CheckCircle, XCircle, Clock, AlertCircle } from "lucide-react";
import { DataTable, type TableColumn, type Action } from "@/modules/shared/ui/components/custom/CustomTable";
import { Badge } from "@/modules/shared/ui/components/badge";
import type { PaymentHistoryDto } from "../../dtos/payment-history.dto";

interface PaymentHistoryTableProps {
  payments: PaymentHistoryDto[];
  onSelectPayment?: (payment: PaymentHistoryDto) => void;
}

export function PaymentHistoryTable({ payments, onSelectPayment }: PaymentHistoryTableProps) {
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-ZA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      { variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ElementType }
    > = {
      Paid: { variant: "default", icon: CheckCircle },
      Pending: { variant: "secondary", icon: Clock },
      Failed: { variant: "destructive", icon: XCircle },
      Cancelled: { variant: "outline", icon: AlertCircle },
    };

    const config = statusConfig[status] || { variant: "outline" as const, icon: AlertCircle };
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const columns: TableColumn<PaymentHistoryDto>[] = [
    {
      key: "createdAt",
      label: "Date",
      render: (row) => (
        <span className="font-medium">{formatDate(row.createdAt)}</span>
      ),
    },
    {
      key: "type",
      label: "Description",
      render: (row) => `${row.type} Payment`,
    },
    {
      key: "totalAmount",
      label: "Amount",
      render: (row) => formatCurrency(row.totalAmount, row.currency),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => getStatusBadge(row.status),
    },
    {
      key: "id",
      label: "Details",
      render: (row) => (
        <span className="text-sm">
          {row.status === "Failed" && row.failureReason && (
            <span className="text-red-600">{row.failureReason}</span>
          )}
          {row.status === "Paid" && row.paidAt && (
            <span className="text-green-600">Paid on {formatDate(row.paidAt)}</span>
          )}
        </span>
      ),
    },
  ];

  const actions: Action<PaymentHistoryDto>[] = [
    {
      label: "View Details",
      onClick: (row) => {
        if (onSelectPayment) {
          onSelectPayment(row);
        }
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={payments}
      actions={actions}
      emptyMessage={
        <div className="py-8">
          <p className="text-muted-foreground mb-2">No payment history found.</p>
          <p className="text-sm text-muted-foreground">
            Your payment transactions will appear here once you make a payment.
          </p>
        </div>
      }
    />
  );
}